import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMainAxios } from '../utils/util';

type Mode = 'login' | 'register' | 'resetpw';
type LoginStep = 'form' | 'otp';

// 로그인 화면에 노출할 탭.
//
// 회원가입·비밀번호찾기는 숨겼다 (2026-09-22) — 기존 가입자만 접속한다.
// 되살리려면 아래에 ['register', '회원가입'] · ['resetpw', '비밀번호찾기'] 를
// 다시 넣고, **서버의 Auth:AllowRegistration / Auth:AllowPasswordReset 도
// 함께** true 로 바꿔야 한다. 한쪽만 바꾸면 메뉴는 보이는데 403 이 난다.
const VISIBLE_MODES: [Mode, string][] = [
  ['login', '로그인'],
];

const LoginPage: React.FC = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  // 비밀번호 찾기
  const [resetStep, setResetStep] = useState<'email' | 'code'>('email');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isReal, setIsReal] = useState(false);
  const [tradePwd, setTradePwd] = useState('');
  // 실전 OTP 단계
  const [loginStep, setLoginStep] = useState<LoginStep>('form');
  const [otpCode, setOtpCode] = useState('');

  const switchMode = (m: Mode) => {
    setMode(m);
    setError(null);
    setSuccess(null);
    setResetStep('email');
    setResetCode('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setLoginStep('form');
    setOtpCode('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (mode === 'register') {
      if (password !== passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
      if (password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
      setSubmitting(true);
      const err = await register(email, password, name);
      setSubmitting(false);
      if (err) setError(err);

    } else if (mode === 'resetpw') {
      if (resetStep === 'email') {
        if (!email) { setError('이메일을 입력해주세요.'); return; }
        setSubmitting(true);
        try {
          const axios = getMainAxios();
          const res = await axios.post('/api/v1/auth/send-reset-code', { email });
          if (res.data?.success) {
            setResetStep('code');
            setSuccess('인증코드가 이메일로 발송되었습니다.');
          } else {
            setError(res.data?.error || '인증코드 발송에 실패했습니다.');
          }
        } catch (err: any) {
          setError(err.response?.data?.error || '인증코드 발송에 실패했습니다.');
        } finally {
          setSubmitting(false);
        }
      } else {
        if (newPassword !== newPasswordConfirm) { setError('새 비밀번호가 일치하지 않습니다.'); return; }
        if (newPassword.length < 6) { setError('새 비밀번호는 6자 이상이어야 합니다.'); return; }
        setSubmitting(true);
        try {
          const axios = getMainAxios();
          const res = await axios.post('/api/v1/auth/reset-password', { email, code: resetCode, newPassword });
          if (res.data?.success) {
            setSuccess('비밀번호가 재설정되었습니다. 로그인해주세요.');
            setTimeout(() => switchMode('login'), 2000);
          } else {
            setError(res.data?.error || '비밀번호 재설정에 실패했습니다.');
          }
        } catch (err: any) {
          setError(err.response?.data?.error || '비밀번호 재설정에 실패했습니다.');
        } finally {
          setSubmitting(false);
        }
      }

    } else {
      // 로그인
      if (loginStep === 'otp') {
        // OTP 검증 → 실전 로그인 완료 (토큰 발급)
        setSubmitting(true);
        try {
          const axios = getMainAxios();
          const res = await axios.post('/api/v1/auth/verify-otp', { email, otp: otpCode, password })   // 서버가 비밀번호도 다시 본다 (2026-10-27);
          if (res.data?.token) {
            localStorage.setItem('fds_isReal', 'true');
            localStorage.setItem('fds_token', res.data.token);
            window.location.reload();
          } else {
            setError(res.data?.error || 'OTP 인증에 실패했습니다.');
          }
        } catch (err: any) {
          setError(err.response?.data?.error || 'OTP 인증에 실패했습니다.');
        } finally {
          setSubmitting(false);
        }
        return;
      }

      if (isReal) {
        // 실전: 자격증명 검증 → OTP 발송 → OTP 입력 단계로 전환
        setSubmitting(true);
        try {
          const axios = getMainAxios();
          // 1단계: 자격증명 검증 (토큰 미발급)
          await axios.post('/api/v1/auth/validate-credentials', {
            email, password, tradePwd: tradePwd || undefined,
          });
          // 2단계: OTP 발송
          await axios.post('/api/v1/auth/send-otp', { email, password });
          setLoginStep('otp');
          setSuccess('텔레그램으로 6자리 인증코드가 발송되었습니다.');
        } catch (err: any) {
          setError(err.response?.data?.error || '로그인에 실패했습니다.');
        } finally {
          setSubmitting(false);
        }
      } else {
        // 모의: 기존 login 흐름
        setSubmitting(true);
        localStorage.setItem('fds_isReal', 'false');
        const err = await login(email, password, undefined);
        setSubmitting(false);
        if (err) setError(err);
      }
    }
  };

  const handleOtpResend = async () => {
    setError(null);
    setSuccess(null);
    try {
      const axios = getMainAxios();
      await axios.post('/api/v1/auth/send-otp', { email, password });
      setSuccess('인증코드가 재발송되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.error || '재발송 실패');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
    borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '4px',
  };

  const submitLabels: Record<string, string> = {
    login: '로그인',
    'login-otp': '인증 완료',
    register: '회원가입',
    'resetpw-email': '인증코드 발송',
    'resetpw-code': '비밀번호 재설정',
  };
  const submitKey = mode === 'resetpw'
    ? `resetpw-${resetStep}`
    : (mode === 'login' && loginStep === 'otp') ? 'login-otp' : mode;

  return (
    <div style={{ minHeight: '100vh', background: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '40px',
        width: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <h1 style={{ textAlign: 'center', color: '#1e3a8a', fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>FDS</h1>
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '13px', marginBottom: '24px' }}>Financial Data Service</p>

        {/* 탭 메뉴
            회원가입·비밀번호찾기는 숨긴다 (2026-09-22, 사용자 요청).
            기존 가입자 외에는 접속을 막는다.

            ⚠ 이건 **표시만** 감추는 것이다. 실제 차단은 서버에서 한다 —
              /auth/register        → Auth:AllowRegistration  (기본 false)
              /auth/send-reset-code → Auth:AllowPasswordReset (기본 false)
              /auth/reset-password  → 〃
            화면만 가리면 curl 로 그대로 호출된다. 다시 열 때는 여기와
            appsettings 를 **함께** 되돌려야 한다. */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
          {(VISIBLE_MODES as [Mode, string][]).map(([m, label]) => (
            <button key={m} onClick={() => switchMode(m)} style={{
              flex: 1, padding: '8px', border: 'none',
              borderBottom: mode === m ? '3px solid #2563eb' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: mode === m ? '#2563eb' : '#9ca3af',
              fontSize: '13px', fontWeight: mode === m ? '700' : '500', cursor: 'pointer',
            }}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* OTP 입력 단계 */}
          {mode === 'login' && loginStep === 'otp' ? (
            <div>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', color: '#991b1b' }}>
                텔레그램으로 전송된 6자리 인증코드를 입력하세요.<br />
                <span style={{ color: '#64748b' }}>유효시간: 5분</span>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>인증코드 (6자리)</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*"
                  value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                  maxLength={6} placeholder="000000" autoFocus
                  style={{ ...inputStyle, letterSpacing: '0.3em', textAlign: 'center', fontSize: '20px' }}
                  onFocus={e => e.target.style.borderColor = '#ef4444'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <button type="button" onClick={handleOtpResend}
                style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', marginBottom: '12px', textDecoration: 'underline' }}>
                인증코드 재발송
              </button>
            </div>
          ) : (
            <>
              {/* 이메일 — 모든 모드 공통 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>이메일</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              {/* 로그인 */}
              {mode === 'login' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>비밀번호</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#3b82f6'}
                      onBlur={e => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>
                  <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>모드:</label>
                    <label style={{ fontSize: '13px', cursor: 'pointer', color: !isReal ? '#22c55e' : '#6b7280', fontWeight: !isReal ? 700 : 400 }}>
                      <input type="radio" checked={!isReal} onChange={() => { setIsReal(false); setTradePwd(''); }} style={{ marginRight: '4px' }} />모의
                    </label>
                    <label style={{ fontSize: '13px', cursor: 'pointer', color: isReal ? '#ef4444' : '#6b7280', fontWeight: isReal ? 700 : 400 }}>
                      <input type="radio" checked={isReal} onChange={() => setIsReal(true)} style={{ marginRight: '4px' }} />실전
                    </label>
                  </div>
                  {isReal && (
                    <>
                      <div style={{ marginBottom: '8px', padding: '8px 12px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', color: '#991b1b' }}>
                        실전 로그인 시 텔레그램으로 OTP 인증코드가 발송됩니다.
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={labelStyle}>거래 비밀번호 (숫자)</label>
                        <input type="password" inputMode="numeric" pattern="[0-9]*" value={tradePwd}
                          onChange={e => setTradePwd(e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="실전 계좌 거래 비밀번호"
                          style={inputStyle}
                          onFocus={e => e.target.style.borderColor = '#ef4444'}
                          onBlur={e => e.target.style.borderColor = '#d1d5db'}
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* 회원가입 */}
              {mode === 'register' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>비밀번호</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                      style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>비밀번호 확인</label>
                    <input type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} required
                      style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>이름</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} required
                      style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                  </div>
                </>
              )}

              {/* 비밀번호 찾기 step2 */}
              {mode === 'resetpw' && resetStep === 'code' && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>인증코드 (6자리)</label>
                    <input type="text" value={resetCode} onChange={e => setResetCode(e.target.value)} required
                      maxLength={6} placeholder="이메일로 받은 6자리 코드"
                      style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>새 비밀번호</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                      style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>새 비밀번호 확인</label>
                    <input type="password" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} required
                      style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#d1d5db'} />
                  </div>
                </>
              )}
            </>
          )}

          {error && (
            <div style={{ padding: '10px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '12px', marginBottom: '16px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ padding: '10px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', color: '#166534', fontSize: '12px', marginBottom: '16px' }}>
              {success}
            </div>
          )}

          <button type="submit" disabled={submitting} style={{
            width: '100%', padding: '12px',
            backgroundColor: submitting ? '#93c5fd' : (mode === 'login' && isReal ? '#dc2626' : '#2563eb'),
            color: 'white', border: 'none', borderRadius: '6px',
            fontSize: '14px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer',
          }}>
            {submitting ? '처리 중...' : submitLabels[submitKey]}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
